/**
 * UserPermissionEditor - Pro user-bazlı ekran izin editörü.
 *
 * Admin bir kullanıcının hangi ekranlara erişebileceğini ekran bazlı
 * olarak yönetir. Rol preset'i + per-screen override karması.
 *
 * Kullanım:
 *   <UserPermissionEditor user={agent} open={open} onOpenChange={setOpen} />
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  ShieldCheck, Loader2, RotateCcw, CheckSquare, Square,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  SCREENS,
  AREA_LABELS,
  ROLE_DEFAULTS,
  getScreensByArea,
} from '@/lib/permissions';
import { base44 } from '@/api/base44Client';
import { cn } from '@/lib/utils';

const AREA_ORDER = ['admin', 'agent', 'warehouse', 'shipping'];
const AREA_COLORS = {
  admin: 'text-purple-600 bg-purple-50 border-purple-200',
  agent: 'text-blue-600 bg-blue-50 border-blue-200',
  warehouse: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  shipping: 'text-amber-600 bg-amber-50 border-amber-200',
};

export default function UserPermissionEditor({ user, open, onOpenChange }) {
  const queryClient = useQueryClient();

  // local state: { screenName: true | false } — null/undefined = use default
  const [overrides, setOverrides] = useState({});

  const userRole = (user?.rolle || user?.role || 'agent').toLowerCase();
  const roleDefaults = ROLE_DEFAULTS[userRole] || [];

  // Initial load — kullanıcının mevcut override'larını state'e koy
  useEffect(() => {
    if (!user) return;
    let initial = user.screen_permissions || {};
    if (typeof initial === 'string') {
      try {
        initial = JSON.parse(initial);
      } catch {
        initial = {};
      }
    }
    setOverrides(initial || {});
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: async (newOverrides) => {
      // Sadece değişen değerleri kaydet — eğer override role default ile aynıysa,
      // alanı kaldır (cleaner JSONB).
      const cleaned = {};
      for (const [screen, value] of Object.entries(newOverrides)) {
        const isDefault = roleDefaults.includes(screen);
        if (value === null || value === undefined) continue;
        if (value === isDefault) continue; // aynı, override gerekmez
        cleaned[screen] = !!value;
      }
      return base44.users.updateAgent(user.id, { screen_permissions: cleaned });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['all-users']);
      toast.success('Berechtigungen gespeichert', {
        description: 'Beim nächsten Login werden die neuen Berechtigungen aktiv.',
      });
      onOpenChange(false);
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // Resolved view: hangi ekranlar şu an açık?
  const isAllowed = (screen) => {
    if (screen in overrides) return !!overrides[screen];
    return roleDefaults.includes(screen);
  };

  const isOverridden = (screen) => screen in overrides && overrides[screen] !== roleDefaults.includes(screen);

  const toggleScreen = (screen) => {
    setOverrides((prev) => {
      const next = { ...prev };
      const currentlyAllowed = isAllowed(screen);
      next[screen] = !currentlyAllowed;
      return next;
    });
  };

  // Toplu aksiyonlar
  const setAreaAccess = (area, allowed) => {
    const screens = getScreensByArea(area).map((s) => s.key);
    setOverrides((prev) => {
      const next = { ...prev };
      for (const s of screens) {
        next[s] = allowed;
      }
      return next;
    });
  };

  const resetToRoleDefaults = () => {
    setOverrides({});
    toast.info('Auf Rollen-Standard zurückgesetzt — noch nicht gespeichert.');
  };

  const groupedScreens = useMemo(() => {
    return AREA_ORDER.map((area) => ({
      area,
      label: AREA_LABELS[area],
      screens: getScreensByArea(area),
    }));
  }, []);

  const allowedCount = Object.keys(SCREENS).filter(isAllowed).length;
  const totalCount = Object.keys(SCREENS).length;

  if (!user) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
        <div className="sticky top-0 bg-white border-b z-10 px-6 pt-6 pb-4">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-purple-600" />
              Bildschirm-Berechtigungen
            </SheetTitle>
          </SheetHeader>
          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm font-semibold text-slate-900">
              {user.name || [user.vorname, user.nachname].filter(Boolean).join(' ') || user.email}
            </p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className={cn('text-[10px] uppercase font-bold', AREA_COLORS[userRole] || '')}>
                Rolle: {userRole}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {allowedCount} / {totalCount} Bildschirme
              </Badge>
            </div>
          </div>

          {userRole === 'admin' && (
            <div className="mt-3 flex items-start gap-2 p-2.5 rounded-md bg-purple-50 border border-purple-200 text-xs text-purple-800">
              <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Administratoren</strong> haben standardmäßig Zugriff auf alle Bereiche. Overrides werden ignoriert.
              </span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 space-y-5">
          {groupedScreens.map((group) => {
            const groupAllAllowed = group.screens.every((s) => isAllowed(s.key));
            const groupNoneAllowed = group.screens.every((s) => !isAllowed(s.key));
            return (
              <div key={group.area}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    {group.label}
                  </h3>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAreaAccess(group.area, true)}
                      disabled={groupAllAllowed}
                      className="h-7 px-2 text-[10px]"
                      title="Alle aktivieren"
                    >
                      <CheckSquare className="w-3 h-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAreaAccess(group.area, false)}
                      disabled={groupNoneAllowed}
                      className="h-7 px-2 text-[10px]"
                      title="Alle deaktivieren"
                    >
                      <Square className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  {group.screens.map((screen) => {
                    const allowed = isAllowed(screen.key);
                    const overridden = isOverridden(screen.key);
                    const isDefault = roleDefaults.includes(screen.key);
                    return (
                      <div
                        key={screen.key}
                        className={cn(
                          'flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors',
                          allowed ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50/30'
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {screen.label}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] text-slate-400 font-mono">{screen.key}</span>
                            {isDefault && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 bg-slate-100 text-slate-500 border-slate-200">
                                Standard
                              </Badge>
                            )}
                            {overridden && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 bg-amber-100 text-amber-700 border-amber-200">
                                Override
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Switch
                          checked={allowed}
                          onCheckedChange={() => toggleScreen(screen.key)}
                          disabled={userRole === 'admin'}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetToRoleDefaults}
            disabled={updateMutation.isPending || userRole === 'admin'}
            className="min-h-[44px]"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Auf Standard
          </Button>
          <div className="flex-1" />
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
            className="min-h-[44px]"
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={() => updateMutation.mutate(overrides)}
            disabled={updateMutation.isPending || userRole === 'admin'}
            className="bg-purple-600 hover:bg-purple-700 min-h-[44px]"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Speichere...
              </>
            ) : (
              <>Speichern</>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
